import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { ILocation } from "@dc-ireland/shared"
import AdminAlerts from "../../components/admin/AdminAlerts"

const emptyForm = {
  slug: "",
  name: "",
  addressLines: "",
  timesLines: "",
  pagePath: "",
  sortOrder: "0",
}

const AdminLocations = () => {
  const [locations, setLocations] = useState<ILocation[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadLocations = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiClient.getLocations()
      setLocations(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load locations")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLocations()
  }, [])

  const resetForm = () => {
    setForm(emptyForm)
    setEditingSlug(null)
  }

  const startEdit = (location: ILocation) => {
    setEditingSlug(location.slug)
    setForm({
      slug: location.slug,
      name: location.name,
      addressLines: location.address.join("\n"),
      timesLines: location.times.join("\n"),
      pagePath: location.pagePath ?? "",
      sortOrder: String(location.sortOrder ?? 0),
    })
  }

  const parseLines = (value: string) =>
    value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    const payload = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      address: parseLines(form.addressLines),
      times: parseLines(form.timesLines),
      pagePath: form.pagePath.trim() || undefined,
      sortOrder: Number(form.sortOrder) || 0,
    }

    try {
      if (editingSlug) {
        await apiClient.updateLocation(editingSlug, payload)
        setSuccess("Location updated.")
      } else {
        await apiClient.createLocation(payload)
        setSuccess("Location created.")
      }
      resetForm()
      await loadLocations()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save location")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (slug: string) => {
    if (!confirm(`Delete location "${slug}"?`)) return
    try {
      setError(null)
      await apiClient.deleteLocation(slug)
      setSuccess("Location deleted.")
      if (editingSlug === slug) resetForm()
      await loadLocations()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete location")
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">Locations</h2>
        <p className="mt-1 text-secondary">
          Branch addresses and service times shown in the Our Locations section.
        </p>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          <h3 className="card-title">{editingSlug ? `Edit ${editingSlug}` : "Add Location"}</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="form-control">
              <span className="label-text">Slug *</span>
              <input
                className="input input-bordered w-full"
                required
                disabled={Boolean(editingSlug)}
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="dublin"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Display name *</span>
              <input
                className="input input-bordered w-full"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Dominion City Dublin"
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Address lines *</span>
              <span className="label-text-alt">One line per row</span>
              <textarea
                className="textarea textarea-bordered w-full"
                rows={3}
                required
                value={form.addressLines}
                onChange={(e) => setForm({ ...form, addressLines: e.target.value })}
                placeholder={"Castleknock Community Center\nBlanchardstown, Dublin\nD15 T3PN"}
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Service times *</span>
              <span className="label-text-alt">One line per row</span>
              <textarea
                className="textarea textarea-bordered w-full"
                rows={3}
                required
                value={form.timesLines}
                onChange={(e) => setForm({ ...form, timesLines: e.target.value })}
                placeholder={"Sunday • 10:00 AM\nWednesday • 7:30 PM (online)"}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Sort order</span>
              <input
                type="number"
                className="input input-bordered w-full"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Page path (optional)</span>
              <input
                className="input input-bordered w-full"
                value={form.pagePath}
                onChange={(e) => setForm({ ...form, pagePath: e.target.value })}
                placeholder="/dublin"
              />
            </label>
          </div>

          <div className="card-actions justify-end">
            {editingSlug && (
              <button type="button" onClick={resetForm} className="btn btn-ghost">
                Cancel edit
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : editingSlug ? "Update Location" : "Create Location"}
            </button>
          </div>
        </div>
      </form>

      <div className="overflow-x-auto">
        <h3 className="mb-4 text-lg font-semibold">Existing Locations</h3>
        {loading ? (
          <span className="loading loading-spinner loading-md"></span>
        ) : locations.length === 0 ? (
          <p className="text-secondary">No locations yet.</p>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Times</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.slug}>
                  <td>{location.name}</td>
                  <td>{location.slug}</td>
                  <td>{location.times.join(" · ")}</td>
                  <td className="space-x-2">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => startEdit(location)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-error btn-sm"
                      onClick={() => handleDelete(location.slug)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default AdminLocations
